/**
 * Pure async assembler: Dexie → App props (docs/08 §10).
 * Reads non-deleted rows, computes state/grace/obligation, builds view models.
 * Never invents financial values. Label: "Issuer ···suffix".
 */

import type { CardZappDB } from '../db/database';
import { asCents, type Cents } from '../domain/money';
import type { BusinessDate } from '../domain/time';
import type { FinancialObservationInput, TransactionInput } from '../domain/state';
import type { GraceCardInput, DeriveGraceInput } from '../domain/grace';
import { buildCardEligibilityContext, type CardEligibilityContext } from '../domain/eligibility';
import {
  computeCardObligation,
  type AutopayObservationInput,
  type ObligationTransactionInput,
} from '../domain/obligations';
import {
  buildPaymentSafetyCard,
  type BuildSafetyCardInput,
} from '../ui/payment-safety/buildSafetyCard';
import {
  buildStateRefreshCard,
  type BuildRefreshCardInput,
} from '../ui/state-refresh/buildRefreshCard';
import {
  buildEvidenceDashboardCard,
  type BuildEvidenceCardInput,
  type EvidenceAssertionInput,
  type EvidenceSourceInput,
  type CappedRuleInput,
} from '../ui/evidence-dashboard/buildEvidenceCard';
import {
  buildPurchaseHistoryRow,
  type BuildPurchaseHistoryRowInput,
  type PurchaseHistoryRow,
} from '../ui/purchase-history/types';
import {
  buildEverydayGuide,
  type EverydayGuideSnapshot,
  type EverydayGuide,
} from '../domain/recommend/everydayGuide';
import {
  buildWeeklyReview,
  type BuildWeeklyReviewArgs,
  type WeeklyReview,
  type WeeklyReviewCardMeta,
  type WeeklyReviewCashReservationInput,
  type WeeklyReviewPaymentInput,
  type WeeklyReviewTransactionInput,
} from '../domain/weeklyReview';
import {
  buildMonthlyCalendar,
  type MonthlyCalendar,
  type CalendarCardInput,
  type CalendarPaymentInput,
  type CalendarTransactionInput,
} from '../domain/monthlyCalendar';
import {
  computeCheckingReserve,
  type CheckingReserve,
  type CashReservationReserveInput,
} from '../domain/checkingReserve';
import { defaultSettings } from '../domain/recommend/fixtures';
import type { AppProps } from '../ui/App';
import type { PaymentSafetyCardView, RecurringChargeSafetyView } from '../ui/payment-safety/types';
import type { StateRefreshCardView } from '../ui/state-refresh/types';
import type { EvidenceDashboardCardView } from '../ui/evidence-dashboard/types';
import type { QuickPurchaseCardOption } from '../ui/quick-purchase';
import type {
  CardLocal,
  CardFinancialObservationLocal,
  CardAutopayObservationLocal,
  SettingsLocal,
  StatementCycleLocal,
  CardPaymentLocal,
  PaymentAllocationLocal,
  PaymentHistoryCoverageLocal,
  TransactionLocal,
  GraceConfirmationLocal,
  ReviewItemLocal,
  RewardRuleLocal,
  CashReservationLocal,
} from '../schemas/entities';
import type {
  RankCardsInput,
  PurchaseQueryInput,
  RankCardInput,
  SettingsInput,
  RewardRuleInput,
  CapPeriodInput,
  BenefitInput,
  OfferInput,
  MerchantInput,
  CategoryObservationInput,
} from '../domain/recommend/types';
import type {
  StatementCycleInput,
  PaymentAllocationInput,
  PaymentHistoryCoverageInput,
} from '../domain/cycles';
import type { CardPaymentInput } from '../domain/state';
import type { RewardCapPeriod } from '../schemas/local-only';

export type BuildAppPropsResult = Omit<
  AppProps,
  | 'onConfirmPurchase'
  | 'onSubmitRefresh'
  | 'onCorrectCategory'
  | 'onFlagRecurring'
  | 'onPlanWeeklyPayment'
  | 'onUpdateCheckingBalance'
  | 'syncStatus'
>;

function cardLabel(card: CardLocal): string {
  return `${card.issuer} ···${card.display_suffix}`;
}

function liveCards(cards: readonly CardLocal[]): CardLocal[] {
  return cards.filter((c) => c.deleted_at === null);
}

function toCents(v: bigint | null): Cents | null {
  return v === null ? null : asCents(v);
}

function obsToFinancialInput(o: CardFinancialObservationLocal): FinancialObservationInput {
  return {
    id: o.id,
    card_id: o.card_id,
    deleted_at: o.deleted_at,
    observed_at: o.observed_at,
    source: o.source,
    evidence_source_id: o.evidence_source_id,
    current_balance_cents: toCents(o.current_balance_cents),
    pending_balance_cents: toCents(o.pending_balance_cents),
    statement_balance_cents: toCents(o.statement_balance_cents),
    minimum_payment_cents: toCents(o.minimum_payment_cents),
    available_credit_cents: toCents(o.available_credit_cents),
    credit_limit_cents: toCents(o.credit_limit_cents),
    cash_advance_limit_cents: toCents(o.cash_advance_limit_cents),
    payment_due_date: o.payment_due_date,
    statement_closing_date: o.statement_closing_date,
    next_closing_date: o.next_closing_date,
    purchase_apr_bps: o.purchase_apr_bps,
    promotional_apr_bps: o.promotional_apr_bps,
    promotional_apr_end: o.promotional_apr_end,
    account_restricted: o.account_restricted,
    last_payment_date: o.last_payment_date,
    last_payment_amount_cents: toCents(o.last_payment_amount_cents),
    reflects_txn_ids: o.reflects_txn_ids ?? [],
    same_day_ambiguous: o.same_day_ambiguous ?? false,
    retracts_fields: o.retracts_fields ?? [],
    retraction_reason: o.retraction_reason,
  };
}

function txnToStateInput(t: TransactionLocal): TransactionInput {
  return {
    id: t.id,
    card_id: t.card_id,
    deleted_at: t.deleted_at,
    kind: t.kind,
    status: t.status,
    amount_cents: asCents(t.amount_cents),
    posted_date: t.posted_date,
    transaction_date: t.transaction_date,
  };
}

function paymentToInput(p: CardPaymentLocal): CardPaymentInput {
  return {
    id: p.id,
    card_id: p.card_id,
    deleted_at: p.deleted_at,
    status: p.status,
    amount_cents: asCents(p.amount_cents),
    posted_date: p.posted_date,
  };
}

function cycleToInput(c: StatementCycleLocal): StatementCycleInput {
  return {
    id: c.id,
    card_id: c.card_id,
    deleted_at: c.deleted_at,
    cycle_start_date: c.cycle_start_date,
    cycle_end_date: c.cycle_end_date,
    statement_date: c.statement_date,
    payment_due_date: c.payment_due_date,
    is_projected: c.is_projected,
    statement_balance_cents: toCents(c.statement_balance_cents),
    minimum_payment_cents: toCents(c.minimum_payment_cents),
    source_id: c.source_id,
    autopay_scheduled_cents: toCents(c.autopay_scheduled_cents),
    autopay_scheduled_date: c.autopay_scheduled_date,
    status: c.status,
    paid_in_full_override: c.paid_in_full_override,
  };
}

function allocationsToInputs(
  allocations: readonly PaymentAllocationLocal[],
  payments: readonly CardPaymentLocal[],
): PaymentAllocationInput[] {
  const cardByPayment = new Map<string, string>();
  for (const p of payments) {
    cardByPayment.set(p.id, p.card_id);
  }
  const out: PaymentAllocationInput[] = [];
  for (const a of allocations) {
    const cardId = cardByPayment.get(a.payment_id);
    if (cardId === undefined) continue;
    out.push({
      id: a.id,
      card_id: cardId,
      deleted_at: a.deleted_at,
      payment_id: a.payment_id,
      statement_cycle_id: a.statement_cycle_id,
      transaction_id: a.transaction_id,
      amount_cents: asCents(a.amount_cents),
    });
  }
  return out;
}

function coverageToInput(c: PaymentHistoryCoverageLocal): PaymentHistoryCoverageInput {
  return {
    card_id: c.card_id,
    deleted_at: c.deleted_at,
    covers_from: c.covers_from,
    covers_through: c.covers_through,
    is_complete: c.is_complete,
  };
}

function autopayToInput(a: CardAutopayObservationLocal): AutopayObservationInput {
  return {
    card_id: a.card_id,
    deleted_at: a.deleted_at,
    observed_at: a.observed_at,
    enabled: a.enabled,
    autopay_kind: a.autopay_kind,
    fixed_amount_cents: toCents(a.fixed_amount_cents),
  };
}

function txnToObligationInput(t: TransactionLocal): ObligationTransactionInput {
  return {
    id: t.id,
    card_id: t.card_id,
    deleted_at: t.deleted_at,
    kind: t.kind,
    status: t.status,
    amount_cents: asCents(t.amount_cents),
    posted_date: t.posted_date,
    transaction_date: t.transaction_date,
    statement_cycle_id: t.statement_cycle_id,
  };
}

function settingsToInput(s: SettingsLocal | null): SettingsInput {
  if (s === null) return defaultSettings();
  return {
    utilization_warning_bps: s.utilization_warning_bps,
    utilization_hard_bps: s.utilization_hard_bps,
    require_active_grace_for_spending: s.require_active_grace_for_spending,
    allow_interest_bearing_recommendations: s.allow_interest_bearing_recommendations,
    treat_likely_active_as_eligible: s.treat_likely_active_as_eligible,
    min_confidence_to_recommend: s.min_confidence_to_recommend,
    assumed_worst_apr_bps: s.assumed_worst_apr_bps,
    assume_pay_before_close: s.assume_pay_before_close,
    financial_staleness_days: s.financial_staleness_days,
  };
}

function cardToGraceInput(card: CardLocal): GraceCardInput {
  return {
    id: card.id,
    status: card.status,
    grace_exists: card.grace_exists,
    grace_restore: card.grace_restore,
  };
}

function latestNonNullMinPayment(
  cardId: string,
  observations: readonly CardFinancialObservationLocal[],
): { cents: Cents | null; observedAt: string | null } {
  let result: { cents: Cents | null; observedAt: string | null } = {
    cents: null,
    observedAt: null,
  };
  for (const o of observations) {
    if (o.card_id !== cardId || o.deleted_at !== null) continue;
    if (o.minimum_payment_cents === null) continue;
    if (result.observedAt === null || o.observed_at > result.observedAt) {
      result = { cents: asCents(o.minimum_payment_cents), observedAt: o.observed_at };
    }
  }
  return result;
}

function latestAutopayObs(
  cardId: string,
  observations: readonly CardAutopayObservationLocal[],
): CardAutopayObservationLocal | null {
  let best: CardAutopayObservationLocal | null = null;
  for (const o of observations) {
    if (o.card_id !== cardId || o.deleted_at !== null) continue;
    if (best === null || o.observed_at > best.observed_at) best = o;
  }
  return best;
}

function latestPostedPayment(
  cardId: string,
  payments: readonly CardPaymentLocal[],
): { amount_cents: Cents; posted_date: string; observed_at: string } | null {
  let best: CardPaymentLocal | null = null;
  for (const p of payments) {
    if (p.card_id !== cardId || p.deleted_at !== null) continue;
    if (p.status !== 'posted' || p.posted_date === null) continue;
    if (best === null || (best.posted_date !== null && p.posted_date > best.posted_date)) {
      best = p;
    }
  }
  if (best === null || best.posted_date === null) return null;
  return {
    amount_cents: asCents(best.amount_cents),
    posted_date: best.posted_date,
    observed_at: best.updated_at,
  };
}

function newestAccountRestricted(
  cardId: string,
  observations: readonly CardFinancialObservationLocal[],
  afterTimestamp: string | null,
): string | null {
  if (afterTimestamp === null) return null;
  for (const o of observations) {
    if (o.card_id !== cardId || o.deleted_at !== null) continue;
    if (o.account_restricted !== true) continue;
    if (o.observed_at > afterTimestamp) return o.observed_at;
  }
  return null;
}

function newestConfirmation(
  cardId: string,
  confirmations: readonly GraceConfirmationLocal[],
): GraceConfirmationLocal | null {
  let best: GraceConfirmationLocal | null = null;
  for (const c of confirmations) {
    if (c.card_id !== cardId || c.deleted_at !== null) continue;
    if (best === null || c.confirmed_at > best.confirmed_at) best = c;
  }
  return best;
}

function newestObservedCurrentBalance(
  cardId: string,
  observations: readonly CardFinancialObservationLocal[],
): Cents | null {
  let bestAt: string | null = null;
  let balance: Cents | null = null;
  for (const o of observations) {
    if (o.card_id !== cardId || o.deleted_at !== null) continue;
    if (o.current_balance_cents === null) continue;
    if (bestAt === null || o.observed_at > bestAt) {
      bestAt = o.observed_at;
      balance = asCents(o.current_balance_cents);
    }
  }
  return balance;
}

function cardReviewItems(cardId: string, items: readonly ReviewItemLocal[]): ReviewItemLocal[] {
  return items.filter((r) => r.deleted_at === null && (r.card_id === cardId || r.card_id === null));
}

function cashReservedForCard(cardId: string, reservations: readonly CashReservationLocal[]): Cents {
  let sum = 0n;
  for (const r of reservations) {
    if (r.deleted_at !== null || r.card_id !== cardId) continue;
    if (r.status !== 'reserved') continue;
    sum += r.amount_reserved_cents;
  }
  return asCents(sum);
}

function hasUnresolvedPastDue(cardId: string, cycles: readonly StatementCycleLocal[]): boolean {
  return cycles.some(
    (c) => c.card_id === cardId && c.deleted_at === null && c.status === 'past_due',
  );
}

function ruleToInput(r: RewardRuleLocal): RewardRuleInput {
  return {
    id: r.id,
    card_id: r.card_id,
    scope: r.scope,
    reward_rate_bps: r.reward_rate_bps,
    milli_points_per_dollar: r.milli_points_per_dollar,
    milli_cents_per_point: r.milli_cents_per_point,
    category: r.category,
    merchant_match: r.merchant_match,
    merchant_names: r.merchant_names,
    channel: r.channel,
    geography: r.geography,
    requires_activation: r.requires_activation,
    activated: r.activated,
    requires_membership: r.requires_membership,
    membership_active: r.membership_active,
    spend_cap_cents: toCents(r.spend_cap_cents),
    post_cap_rate_bps: r.post_cap_rate_bps,
    valid_from: r.valid_from,
    valid_until: r.valid_until,
    mutually_exclusive_with: r.mutually_exclusive_with ?? [],
    stacking: r.stacking,
    priority: r.priority,
    confidence: r.confidence,
    last_verified_at: r.last_verified_at,
  };
}

function capPeriodToInput(r: RewardCapPeriod): CapPeriodInput | null {
  const ruleId = (r as unknown as { reward_rule_id?: string }).reward_rule_id;
  const remaining = (r as unknown as { remaining_cap_cents?: bigint | number }).remaining_cap_cents;
  if (ruleId === undefined || remaining === undefined) return null;
  return {
    reward_rule_id: ruleId,
    remaining_cap_cents:
      typeof remaining === 'bigint' ? asCents(remaining) : asCents(BigInt(remaining)),
  };
}

/**
 * Build App props from local Dexie snapshot.
 * Reads all tables once, computes views. Never blocks on network.
 */
export async function buildAppPropsFromDb(
  db: CardZappDB,
  asOf: BusinessDate,
): Promise<BuildAppPropsResult> {
  const allCards = await db.cards.toArray();
  const allFinObs = await db.card_financial_observations.toArray();
  const allAutopayObs = await db.card_autopay_observations.toArray();
  const allCycles = await db.statement_cycles.toArray();
  const allPayments = await db.card_payments.toArray();
  const allAllocations = await db.payment_allocations.toArray();
  const allCoverage = await db.payment_history_coverage.toArray();
  const allTransactions = await db.transactions.toArray();
  const allRecurring = await db.recurring_charges.toArray();
  const allConfirmations = await db.grace_confirmations.toArray();
  const allReviewItems = await db.review_items.toArray();
  const allRewardRules = await db.reward_rules.toArray();
  const allCapPeriods = await db.reward_cap_periods.toArray();
  const allAssertions = await db.field_assertions.toArray();
  const allSources = await db.evidence_sources.toArray();
  const allCashRes = await db.cash_reservations.toArray();
  const allSettings = await db.settings.toArray();
  const allBenefits = await db.benefits.toArray();
  const allOffers = await db.offers.toArray();
  const allMerchants = await db.merchants.toArray();
  const allCategoryObs = await db.merchant_category_observations.toArray();

  const cards: CardLocal[] = liveCards(allCards);
  const settingsRow = allSettings.find((s) => s.deleted_at === null) ?? null;
  const settings = settingsToInput(settingsRow);

  const finObsInputs = allFinObs.map(obsToFinancialInput);
  const txnStateInputs = allTransactions.map(txnToStateInput);
  const paymentInputs = allPayments.map(paymentToInput);
  const cycleInputs = allCycles.map(cycleToInput);
  const allocationInputs = allocationsToInputs(allAllocations, allPayments);
  const coverageInputs = allCoverage.map(coverageToInput);
  const autopayInputs = allAutopayObs.map(autopayToInput);
  const txnObligationInputs = allTransactions.map(txnToObligationInput);
  const rewardRuleInputs = allRewardRules.filter((r) => r.deleted_at === null).map(ruleToInput);
  const capPeriodInputs = allCapPeriods
    .map(capPeriodToInput)
    .filter((c): c is CapPeriodInput => c !== null);
  const benefitInputs: BenefitInput[] = allBenefits
    .filter((b) => b.deleted_at === null)
    .map((b) => ({
      card_id: b.card_id,
      kind: b.kind,
      confirmed_absent: b.confirmed_absent,
      position: b.position,
    }));
  const offerInputs: OfferInput[] = allOffers
    .filter((o) => o.deleted_at === null)
    .map((o) => ({
      id: o.id,
      card_id: o.card_id,
      merchant_id: o.merchant_id,
      kind: o.kind,
      value_bps: o.value_bps,
      value_cents: toCents(o.value_cents),
      minimum_spend_cents: toCents(o.minimum_spend_cents),
      maximum_reward_cents: toCents(o.maximum_reward_cents),
      activation_required: o.activation_required,
      activated: o.activated,
      valid_from: o.valid_from,
      valid_until: o.valid_until,
      channel: o.channel,
      confidence: o.confidence,
      last_verified_at: o.last_verified_at,
    }));
  const categoryObsInputs: CategoryObservationInput[] = allCategoryObs
    .filter((o) => o.deleted_at === null)
    .map((o) => ({
      merchant_id: o.merchant_id,
      channel: o.channel,
      implied_category: o.implied_category,
    }));

  const cardLabels: Record<string, string> = {};
  const eligibilityByCard = new Map<string, CardEligibilityContext>();
  const safetyCards: PaymentSafetyCardView[] = [];
  const refreshCards: StateRefreshCardView[] = [];
  const evidenceCards: EvidenceDashboardCardView[] = [];
  const purchaseCards: QuickPurchaseCardOption[] = [];
  const rankCardInputs: RankCardInput[] = [];

  for (const card of cards) {
    const label = cardLabel(card);
    cardLabels[card.id] = label;

    const confirmation = newestConfirmation(card.id, allConfirmations);
    const accountRestrictedAfter =
      confirmation !== null
        ? newestAccountRestricted(card.id, allFinObs, confirmation.confirmed_at)
        : null;

    const graceInput: DeriveGraceInput = {
      card: cardToGraceInput(card),
      confirmation,
      cycles: cycleInputs,
      payments: paymentInputs,
      allocations: allocationInputs,
      coverage: coverageInputs,
      reviewItems: cardReviewItems(card.id, allReviewItems),
      accountRestrictedAfter,
      asOf,
      observedCurrentBalanceCents: newestObservedCurrentBalance(card.id, allFinObs),
    };

    const eligibility = buildCardEligibilityContext({
      stateArgs: {
        cardId: card.id,
        asOf,
        observations: finObsInputs,
        transactions: txnStateInputs,
        payments: paymentInputs,
      },
      graceArgs: graceInput,
    });
    eligibilityByCard.set(card.id, eligibility);

    const obligation = computeCardObligation({
      cardId: card.id,
      asOf,
      cycles: cycleInputs,
      payments: paymentInputs,
      allocations: allocationInputs,
      coverage: coverageInputs,
      transactions: txnObligationInputs,
      autopayObservations: autopayInputs,
    });

    const autopayObs = latestAutopayObs(card.id, allAutopayObs);
    const minPayment = latestNonNullMinPayment(card.id, allFinObs);

    const safetyInput: BuildSafetyCardInput = {
      card_id: card.id,
      label,
      statement_balance_cents: eligibility.state.observed.statement_balance_cents,
      statement_balance_observed_at: eligibility.state.observed.statement_balance_cents_observed_at,
      payment_due_date: eligibility.state.observed.payment_due_date,
      payment_due_date_observed_at: eligibility.state.observed.payment_due_date_observed_at,
      minimum_payment_cents: minPayment.cents,
      minimum_payment_observed_at: minPayment.observedAt,
      autopay: {
        enabled: autopayObs?.enabled ?? null,
        kind: autopayObs?.autopay_kind ?? 'unknown',
        amount_cents:
          autopayObs?.fixed_amount_cents !== null && autopayObs?.fixed_amount_cents !== undefined
            ? asCents(autopayObs.fixed_amount_cents)
            : null,
        observed_at: autopayObs?.observed_at ?? null,
      },
      obligation,
      grace: eligibility.grace,
      cash_reserved_cents: cashReservedForCard(card.id, allCashRes),
      last_posted_payment: latestPostedPayment(card.id, allPayments),
    };
    safetyCards.push(buildPaymentSafetyCard(safetyInput));

    const refreshInput: BuildRefreshCardInput = {
      card_id: card.id,
      label,
      state: eligibility.state,
      financial_staleness_days: settings.financial_staleness_days,
      minimum_payment_cents: minPayment.cents,
      minimum_payment_observed_at: minPayment.observedAt,
    };
    refreshCards.push(buildStateRefreshCard(refreshInput));

    const cardAssertions: EvidenceAssertionInput[] = allAssertions
      .filter((a) => a.entity_id === card.id && a.deleted_at === null)
      .map((a) => ({
        id: a.id,
        entity_id: a.entity_id,
        field_name: a.field_name,
        asserted_value: a.asserted_value as string | number | boolean | null,
        evidence_source_id: a.evidence_source_id,
        confidence: a.confidence,
        verified_at: a.verified_at,
        superseded_at: a.superseded_at,
        deleted_at: a.deleted_at,
      }));
    const cardSources: EvidenceSourceInput[] = allSources
      .filter((s) => s.deleted_at === null)
      .map((s) => ({ id: s.id, kind: s.kind, title: s.title }));
    const cappedRules: CappedRuleInput[] = rewardRuleInputs
      .filter((r) => r.card_id === card.id && r.spend_cap_cents !== null)
      .map((r) => {
        const cap = capPeriodInputs.find((c) => c.reward_rule_id === r.id);
        return {
          id: r.id,
          category: r.category,
          spend_cap_cents: r.spend_cap_cents as Cents,
          remaining_cap_cents: cap?.remaining_cap_cents ?? null,
        };
      });
    const evidenceInput: BuildEvidenceCardInput = {
      card_id: card.id,
      label,
      assertions: cardAssertions,
      sources: cardSources,
      capped_rules: cappedRules,
    };
    evidenceCards.push(buildEvidenceDashboardCard(evidenceInput));

    purchaseCards.push({ card_id: card.id, label });

    rankCardInputs.push({
      card_id: card.id,
      status: card.status as RankCardInput['status'],
      spend_policy: card.spend_policy as RankCardInput['spend_policy'],
      network: card.network as RankCardInput['network'],
      closed_loop_merchant: card.closed_loop_merchant,
      foreign_txn_fee_bps: card.foreign_txn_fee_bps,
      purchase_apr_bps: card.purchase_apr_bps,
      disposition: card.disposition as RankCardInput['disposition'],
      owner: card.owner as RankCardInput['owner'],
      annual_fee_cents: toCents(card.annual_fee_cents),
      unresolved_past_due: hasUnresolvedPastDue(card.id, allCycles),
      foreign_txn_fee_verified_at: null,
      eligibility,
    });
  }

  const purchaseHistory: PurchaseHistoryRow[] = [];
  const liveTxns = allTransactions.filter((t) => t.deleted_at === null && t.kind === 'purchase');
  for (const t of liveTxns) {
    const card = cards.find((c) => c.id === t.card_id);
    if (card === undefined) continue;
    const historyInput: BuildPurchaseHistoryRowInput = {
      transaction_id: t.id,
      merchant_name: t.merchant_name_raw,
      merchant_id: t.merchant_id,
      amount_cents: asCents(t.amount_cents),
      transaction_date: t.transaction_date,
      category: t.category,
      category_src: t.category_src,
      channel: t.channel,
      card_label: cardLabel(card),
      card_suffix: card.display_suffix,
      expected_reward_cents: toCents(t.expected_reward_cents),
      description: t.description,
      notes: t.notes,
      recurring_charge_id: t.recurring_charge_id,
    };
    purchaseHistory.push(buildPurchaseHistoryRow(historyInput));
  }

  const recurringCharges: RecurringChargeSafetyView[] = allRecurring
    .filter((r) => r.deleted_at === null && r.active)
    .map((r) => ({
      id: r.id,
      merchant_name: r.merchant_name,
      expected_amount_cents: toCents(r.expected_amount_cents),
      next_expected_date: r.next_expected_date,
      assigned_card_label:
        r.assigned_card_id !== null ? (cardLabels[r.assigned_card_id] ?? null) : null,
      frequency: r.frequency,
    }));

  const buildRankInput = (query: PurchaseQueryInput): RankCardsInput => {
    const merchantRow =
      query.merchant_id !== null
        ? allMerchants.find((m) => m.id === query.merchant_id && m.deleted_at === null)
        : undefined;
    const merchant: MerchantInput | null =
      merchantRow !== undefined
        ? {
            id: merchantRow.id,
            normalized_name: merchantRow.normalized_name,
            default_category: merchantRow.default_category,
            online_category: merchantRow.online_category,
            in_store_category: merchantRow.in_store_category,
            is_intermediary: merchantRow.is_intermediary,
            category_confidence: merchantRow.category_confidence,
          }
        : null;
    return {
      query,
      merchant,
      cards: rankCardInputs,
      rewardRules: rewardRuleInputs,
      offers: offerInputs,
      benefits: benefitInputs,
      capPeriods: capPeriodInputs,
      categoryObservations: categoryObsInputs,
      settings,
      asOf,
    };
  };

  let everydayGuide: EverydayGuide | null = null;
  if (cards.length > 0) {
    const snapshot: EverydayGuideSnapshot = {
      cards: rankCardInputs,
      rewardRules: rewardRuleInputs,
      offers: offerInputs,
      benefits: benefitInputs,
      capPeriods: capPeriodInputs,
      categoryObservations: categoryObsInputs,
      settings,
      asOf,
    };
    everydayGuide = buildEverydayGuide({
      snapshot,
      cardLabels,
      computedAt: asOf,
    });
  }

  const weeklyMeta: WeeklyReviewCardMeta[] = cards.map((card) => {
    const elig = eligibilityByCard.get(card.id);
    const obl = computeCardObligation({
      cardId: card.id,
      asOf,
      cycles: cycleInputs,
      payments: paymentInputs,
      allocations: allocationInputs,
      coverage: coverageInputs,
      transactions: txnObligationInputs,
      autopayObservations: autopayInputs,
    });
    return {
      card_id: card.id,
      label: cardLabel(card),
      due_now_cents: obl.due_now_cents,
      next_due_date: obl.next_due_date,
      grace_status: elig?.grace.status ?? 'unknown',
      grace_reason: elig?.grace.reason ?? 'Unknown',
    };
  });

  const weeklyTxns: WeeklyReviewTransactionInput[] = allTransactions
    .filter((t) => t.deleted_at === null)
    .map((t) => ({
      id: t.id,
      card_id: t.card_id,
      deleted_at: t.deleted_at,
      kind: t.kind,
      status: t.status,
      amount_cents: asCents(t.amount_cents),
      posted_date: t.posted_date,
    }));

  const weeklyPayments: WeeklyReviewPaymentInput[] = allPayments
    .filter((p) => p.deleted_at === null)
    .map((p) => ({
      id: p.id,
      card_id: p.card_id,
      deleted_at: p.deleted_at,
      status: p.status,
      amount_cents: asCents(p.amount_cents),
    }));

  const weeklyReservations: WeeklyReviewCashReservationInput[] = allCashRes
    .filter((r) => r.deleted_at === null)
    .map((r) => ({
      id: r.id,
      card_id: r.card_id,
      deleted_at: r.deleted_at,
      status: r.status,
      amount_reserved_cents: asCents(r.amount_reserved_cents),
      transaction_id: r.transaction_id,
    }));

  const weeklyReviewArgs: BuildWeeklyReviewArgs = {
    asOf,
    cards: weeklyMeta,
    transactions: weeklyTxns,
    payments: weeklyPayments,
    reservations: weeklyReservations,
  };
  const weeklyReview: WeeklyReview = buildWeeklyReview(weeklyReviewArgs);

  const checkingReservations: CashReservationReserveInput[] = allCashRes
    .filter((r) => r.deleted_at === null)
    .map((r) => ({
      id: r.id,
      deleted_at: r.deleted_at,
      status: r.status,
      amount_reserved_cents: asCents(r.amount_reserved_cents),
    }));
  const checkingReserve: CheckingReserve = computeCheckingReserve({
    asOf,
    checking_balance_cents:
      settingsRow?.checking_balance_cents !== null &&
      settingsRow?.checking_balance_cents !== undefined
        ? asCents(settingsRow.checking_balance_cents)
        : null,
    checking_balance_updated_at: (settingsRow?.checking_balance_updated_at ??
      null) as BusinessDate | null,
    reservations: checkingReservations,
  });

  const monthlyCards: CalendarCardInput[] = cards.map((card) => {
    const elig = eligibilityByCard.get(card.id);
    const obl = computeCardObligation({
      cardId: card.id,
      asOf,
      cycles: cycleInputs,
      payments: paymentInputs,
      allocations: allocationInputs,
      coverage: coverageInputs,
      transactions: txnObligationInputs,
      autopayObservations: autopayInputs,
    });
    return {
      card_id: card.id,
      label: cardLabel(card),
      due_now_cents: obl.due_now_cents,
      autopay_expected_cents: obl.autopay_expected_cents,
      not_yet_due_cents: obl.not_yet_due_cents,
      grace_status: elig?.grace.status ?? 'unknown',
      grace_reason: elig?.grace.reason ?? 'Unknown',
    };
  });
  const monthlyPayments: CalendarPaymentInput[] = allPayments
    .filter((p) => p.deleted_at === null)
    .map((p) => ({
      id: p.id,
      card_id: p.card_id,
      deleted_at: p.deleted_at,
      status: p.status,
      amount_cents: asCents(p.amount_cents),
      is_autopay: p.is_autopay ?? false,
      posted_date: p.posted_date,
      scheduled_date: p.scheduled_date ?? null,
      payment_date: p.payment_date ?? null,
    }));
  const monthlyTxns: CalendarTransactionInput[] = allTransactions
    .filter((t) => t.deleted_at === null)
    .map((t) => ({
      id: t.id,
      card_id: t.card_id,
      deleted_at: t.deleted_at,
      kind: t.kind,
      status: t.status,
      amount_cents: asCents(t.amount_cents),
      posted_date: t.posted_date,
      transaction_date: t.transaction_date,
      expected_reward_cents: toCents(t.expected_reward_cents),
      actual_reward_cents: toCents(t.actual_reward_cents),
      recommendation_result_id: t.recommendation_result_id,
    }));
  const monthlyCalendar: MonthlyCalendar = buildMonthlyCalendar({
    asOf,
    cards: monthlyCards,
    cycles: cycleInputs,
    payments: monthlyPayments,
    transactions: monthlyTxns,
    allocations: allocationInputs,
  });

  return {
    safetyCards,
    purchaseCards,
    buildRankInput,
    everydayGuide,
    refreshCards,
    evidenceCards,
    purchaseHistory,
    recurringCharges,
    weeklyReview,
    checkingReserve,
    monthlyCalendar,
  };
}
